<template>
  <div class="field">
    <label v-if="label" :for="readonly ? undefined : id" class="field__label">
      {{ label }}
      <span v-if="required" class="field__required" aria-hidden="true">*</span>
    </label>

    <div class="field__control" :class="{ 'field__control--invalid': error }">
      <slot :id="id" :invalid="Boolean(error)" />
    </div>

    <!-- role=alert so screen readers announce the problem as it appears -->
    <p v-if="error" class="field__error" role="alert">{{ error }}</p>
    <p v-else-if="hint" class="field__hint">{{ hint }}</p>
  </div>
</template>

<script setup>
import { provide, useId } from 'vue'

import { FIELD_ID } from '../utils/fieldId'

defineProps({
  label: String,
  error: String,
  hint: String,
  required: Boolean,
  // Dentro no hay un control, sino un valor calculado: un margen, un total.
  // Sin esto la etiqueta llevaria un `for` hacia algo que no se puede enfocar,
  // que es la misma etiqueta rota que arregla el `provide` de aqui abajo.
  readonly: Boolean,
})

// Stable unique id so <label for> and the control always match, even with
// several instances of the same form on one page.
const id = useId()

// Y se pone a disposición del control que haya dentro.
//
// El slot ya lo ofrecía —`<slot :id="id">`— y de los 890 campos con etiqueta
// del repositorio lo cogía NINGUNO: cada `<label for>` del ERP apuntaba a un id
// que no existe. Pulsar la etiqueta no hacía nada, que es de las primeras cosas
// que hace un formulario, y un lector de pantalla no tenía con qué nombrar el
// control.
//
// Se reparte por `provide` en vez de pedirle a cada pantalla que lo pase: un
// contrato que hay que recordar en 890 sitios es un contrato que no se cumple,
// y este lleva sin cumplirse desde el primer día. Quien SÍ use el slot manda,
// porque el control usa lo inyectado solo cuando nadie le ha dado un id.
//
// Un campo con dos controles dentro les daría el mismo id. Son tres en todo el
// repositorio y en dos de ellos los controles son `v-if`/`v-else`, así que
// nunca coinciden; donde coinciden, el navegador enfoca el primero, que sigue
// siendo mejor que no enfocar nada.
provide(FIELD_ID, id)
</script>

<style scoped>
.field { display: flex; flex-direction: column; gap: var(--space-2); }

.field__label {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--color-text-soft);
  letter-spacing: 0.02em;
}
.field__required { color: var(--color-danger); margin-left: 2px; }

.field__error { font-size: var(--text-xs); color: var(--color-danger); }
.field__hint  { font-size: var(--text-xs); color: var(--color-text-mute); }
</style>
